// Section 2: Algebra — improved and corrected Grade9Ready topic bank
// Covers KS3/GCSE algebra progression from foundation fluency to higher-grade quadratics.

const PAGE_IMAGES = {
  p36_37: "https://customer-assets.emergentagent.com/job_pathway-to-9/artifacts/5r40jvzy_Page36-37.png",
  p38_39: "https://customer-assets.emergentagent.com/job_pathway-to-9/artifacts/voxhmzin_Page38-39.png",
  p40_41: "https://customer-assets.emergentagent.com/job_pathway-to-9/artifacts/tycz374c_Page40-41.png",
  p42_43: "https://customer-assets.emergentagent.com/job_pathway-to-9/artifacts/nm5j8oqg_Page42-43.png",
  p44_45: "https://customer-assets.emergentagent.com/job_pathway-to-9/artifacts/f6erc6ru_Page44-45.png",
  p46_47: "https://customer-assets.emergentagent.com/job_pathway-to-9/artifacts/aso3zg1b_Page46-47.png",
  p48_49: "https://customer-assets.emergentagent.com/job_pathway-to-9/artifacts/0fhx77oz_Page48-49.png",
  p50_51: "https://customer-assets.emergentagent.com/job_pathway-to-9/artifacts/iwdlzfvd_Page50-51.png",
  p52_53: "https://customer-assets.emergentagent.com/job_pathway-to-9/artifacts/vd28q7ju_Page52-53.png",
  p54_55: "https://customer-assets.emergentagent.com/job_pathway-to-9/artifacts/a3mwb3wl_Page54-55.png",
};

export const algebraTopics = [
  {
    id: "2.1",
    title: "Simplifying Terms",
    slug: "simplifying-terms",
    pageRef: "Pages 36-39",
    difficulty: "foundation",
    learn: {
      explanation:
        "Simplifying means collecting like terms together. Like terms have the same letter or letters raised to the same power.",
      keyPoints: [
        "Like terms: 3x and 5x are like terms, but 3x and 3x² are not.",
        "Collect like terms by adding or subtracting their coefficients.",
        "Keep the sign in front of each term when collecting.",
        "Letters multiplied together can be written without multiplication signs: a × b × c = abc.",
        "Power rules: n⁵ × n² = n⁷ and m¹³ ÷ m⁵ = m⁸.",
      ],
      workedExamples: [
        {
          problem: "Simplify 7x + 3 − x − 2",
          image: PAGE_IMAGES.p36_37,
          steps: [
            { step: 1, instruction: "Collect x terms", working: "7x − x = 6x" },
            { step: 2, instruction: "Collect number terms", working: "3 − 2 = 1" },
          ],
          answer: "6x + 1",
        },
        {
          problem: "Simplify 3r × 2s × 2",
          steps: [
            { step: 1, instruction: "Multiply the numbers", working: "3 × 2 × 2 = 12" },
            { step: 2, instruction: "Write the letters together", working: "r × s = rs" },
          ],
          answer: "12rs",
        },
      ],
      whyItWorks:
        "Like terms can be combined because they represent the same type of quantity. For example, 7x − x means 7 lots of x minus 1 lot of x.",
    },
    practice: {
      warmUp: [
        {
          id: "2.1.w1",
          type: "text",
          question: "Simplify r + r + r + r",
          answer: "4r",
          acceptedAnswers: ["4r", "4 r"],
          hint1: "Count the r terms.",
          hint2: "There are 4 lots of r.",
          hint3: "4r",
        },
        {
          id: "2.1.w2",
          type: "text",
          question: "Simplify 2s + 3s − s",
          answer: "4s",
          acceptedAnswers: ["4s", "4 s"],
          hint1: "Use the coefficients: 2 + 3 − 1.",
          hint2: "2 + 3 − 1 = 4.",
          hint3: "4s",
        },
      ],
      level1: [
        {
          id: "2.1.1",
          type: "text",
          question: "Simplify 4x − 2y + 3x + y",
          answer: "7x - y",
          acceptedAnswers: ["7x-y", "7x - y", "7x + -y"],
          hint1: "x terms: 4x + 3x = 7x.",
          hint2: "y terms: −2y + y = −y.",
          hint3: "7x − y",
        },
        {
          id: "2.1.2",
          type: "text",
          question: "Simplify h × h × h",
          answer: "h^3",
          acceptedAnswers: ["h^3", "h³"],
          hint1: "h is multiplied by itself 3 times.",
          hint2: "That means h cubed.",
          hint3: "h³",
        },
      ],
      level2: [
        {
          id: "2.1.3",
          type: "text",
          question: "Simplify 2x² − 2x + 6x − 5 − 3x² + 3x + 7x²",
          answer: "6x^2 + 7x - 5",
          acceptedAnswers: [
            "6x^2 + 7x - 5",
            "6x² + 7x - 5",
            "6x^2+7x-5",
            "6x²+7x-5",
          ],
          hint1: "x² terms: 2 − 3 + 7 = 6.",
          hint2: "x terms: −2 + 6 + 3 = 7.",
          hint3: "6x² + 7x − 5",
        },
      ],
      level3: [
        {
          id: "2.1.4",
          type: "text",
          question: "Simplify n⁵ × n². Write your answer using powers.",
          answer: "n^7",
          acceptedAnswers: ["n^7", "n⁷"],
          hint1: "When multiplying powers with the same base, add the powers.",
          hint2: "5 + 2 = 7.",
          hint3: "n⁷",
        },
        {
          id: "2.1.5",
          type: "text",
          question: "Simplify m¹³ ÷ m⁵. Write your answer using powers.",
          answer: "m^8",
          acceptedAnswers: ["m^8", "m⁸"],
          hint1: "When dividing powers with the same base, subtract the powers.",
          hint2: "13 − 5 = 8.",
          hint3: "m⁸",
        },
      ],
    },
  },

  {
    id: "2.2",
    title: "Expanding Single Brackets",
    slug: "single-brackets",
    pageRef: "Pages 39-40",
    difficulty: "foundation",
    learn: {
      explanation:
        "To expand a single bracket, multiply the term outside by every term inside the bracket.",
      keyPoints: [
        "Multiply the outside term by each term inside the bracket.",
        "Remember: p × q = pq and r × r = r².",
        "A negative outside the bracket changes the sign of every term inside.",
        "After expanding, collect like terms if possible.",
      ],
      workedExamples: [
        {
          problem: "Expand 2(4x + 3)",
          image: PAGE_IMAGES.p38_39,
          steps: [
            { step: 1, instruction: "Multiply 2 by 4x", working: "2 × 4x = 8x" },
            { step: 2, instruction: "Multiply 2 by 3", working: "2 × 3 = 6" },
          ],
          answer: "8x + 6",
        },
        {
          problem: "Expand −2(5t² − 6s³)",
          steps: [
            { step: 1, instruction: "Multiply −2 by 5t²", working: "−2 × 5t² = −10t²" },
            {
              step: 2,
              instruction: "Multiply −2 by −6s³",
              working: "−2 × −6s³ = +12s³",
            },
          ],
          answer: "−10t² + 12s³",
        },
        {
          problem: "Expand and simplify 3(2x + 1) + 2x(4 + 3x)",
          steps: [
            { step: 1, instruction: "Expand the first bracket", working: "3(2x + 1) = 6x + 3" },
            { step: 2, instruction: "Expand the second bracket", working: "2x(4 + 3x) = 8x + 6x²" },
            {
              step: 3,
              instruction: "Collect like terms",
              working: "6x + 3 + 8x + 6x² = 6x² + 14x + 3",
            },
          ],
          answer: "6x² + 14x + 3",
        },
      ],
      whyItWorks:
        "Expanding brackets uses the distributive law: a(b + c) = ab + ac.",
    },
    practice: {
      level1: [
        {
          id: "2.2.1",
          type: "text",
          question: "Expand 3(x + 4)",
          answer: "3x + 12",
          acceptedAnswers: ["3x + 12", "3x+12"],
          hint1: "3 × x = 3x.",
          hint2: "3 × 4 = 12.",
          hint3: "3x + 12",
        },
        {
          id: "2.2.2",
          type: "text",
          question: "Expand 5(2y − 3)",
          answer: "10y - 15",
          acceptedAnswers: ["10y - 15", "10y-15"],
          hint1: "5 × 2y = 10y.",
          hint2: "5 × −3 = −15.",
          hint3: "10y − 15",
        },
      ],
      level2: [
        {
          id: "2.2.3",
          type: "text",
          question: "Expand 2p(q − 3r)",
          answer: "2pq - 6pr",
          acceptedAnswers: ["2pq - 6pr", "2pq-6pr"],
          hint1: "2p × q = 2pq.",
          hint2: "2p × −3r = −6pr.",
          hint3: "2pq − 6pr",
        },
        {
          id: "2.2.4",
          type: "text",
          question: "Expand and simplify 2(5x − 1) − 7x",
          answer: "3x - 2",
          acceptedAnswers: ["3x - 2", "3x-2"],
          hint1: "Expand first: 10x − 2.",
          hint2: "Then simplify 10x − 2 − 7x.",
          hint3: "3x − 2",
        },
      ],
      level3: [
        {
          id: "2.2.5",
          type: "text",
          question: "Expand and simplify 3y(2y + 1) − y(4 + 3y) − 3y(y − 4)",
          answer: "11y",
          acceptedAnswers: ["11y", "11 y"],
          hint1: "Expand to get 6y² + 3y − 4y − 3y² − 3y² + 12y.",
          hint2: "y² terms cancel: 6 − 3 − 3 = 0.",
          hint3: "y terms: 3 − 4 + 12 = 11, so 11y.",
        },
      ],
    },
  },

  {
    id: "2.3",
    title: "Expanding Double Brackets",
    slug: "double-brackets",
    pageRef: "Pages 40-41",
    difficulty: "intermediate",
    learn: {
      explanation:
        "To expand double brackets, multiply every term in the first bracket by every term in the second bracket. Then collect like terms.",
      keyPoints: [
        "FOIL means First, Outside, Inside, Last.",
        "Always collect like terms after expanding.",
        "(a + b)² means (a + b)(a + b), not a² + b².",
        "(a + b)² = a² + 2ab + b².",
      ],
      workedExamples: [
        {
          problem: "Expand (n − 1)(2n + 5)",
          image: PAGE_IMAGES.p40_41,
          steps: [
            { step: 1, instruction: "First", working: "n × 2n = 2n²" },
            { step: 2, instruction: "Outside", working: "n × 5 = 5n" },
            { step: 3, instruction: "Inside", working: "−1 × 2n = −2n" },
            { step: 4, instruction: "Last", working: "−1 × 5 = −5" },
            { step: 5, instruction: "Collect like terms", working: "2n² + 5n − 2n − 5 = 2n² + 3n − 5" },
          ],
          answer: "2n² + 3n − 5",
        },
        {
          problem: "Expand (4x + 1)²",
          steps: [
            { step: 1, instruction: "Write as two brackets", working: "(4x + 1)(4x + 1)" },
            { step: 2, instruction: "Expand", working: "16x² + 4x + 4x + 1" },
            { step: 3, instruction: "Collect like terms", working: "16x² + 8x + 1" },
          ],
          answer: "16x² + 8x + 1",
        },
      ],
      whyItWorks:
        "Every term in one bracket must multiply every term in the other bracket. FOIL is a memory aid that helps you avoid missing a product.",
    },
    practice: {
      level1: [
        {
          id: "2.3.1",
          type: "text",
          question: "Expand (x + 2)(x + 3)",
          answer: "x^2 + 5x + 6",
          acceptedAnswers: ["x^2 + 5x + 6", "x² + 5x + 6", "x^2+5x+6", "x²+5x+6"],
          hint1: "First: x². Outside: 3x.",
          hint2: "Inside: 2x. Last: 6.",
          hint3: "x² + 5x + 6",
        },
        {
          id: "2.3.2",
          type: "text",
          question: "Expand (y − 6)(y + 3)",
          answer: "y^2 - 3y - 18",
          acceptedAnswers: ["y^2 - 3y - 18", "y² - 3y - 18", "y^2-3y-18", "y²-3y-18"],
          hint1: "First: y². Outside: 3y.",
          hint2: "Inside: −6y. Last: −18.",
          hint3: "y² − 3y − 18",
        },
      ],
      level2: [
        {
          id: "2.3.3",
          type: "text",
          question: "Expand (3x + 3)²",
          answer: "9x^2 + 18x + 9",
          acceptedAnswers: ["9x^2 + 18x + 9", "9x² + 18x + 9", "9x^2+18x+9", "9x²+18x+9"],
          hint1: "Write it as (3x + 3)(3x + 3).",
          hint2: "Expand: 9x² + 9x + 9x + 9.",
          hint3: "9x² + 18x + 9",
        },
      ],
      level3: [
        {
          id: "2.3.4",
          type: "text",
          question: "Expand (5x + 2)²",
          answer: "25x^2 + 20x + 4",
          acceptedAnswers: ["25x^2 + 20x + 4", "25x² + 20x + 4", "25x^2+20x+4", "25x²+20x+4"],
          hint1: "Write it as (5x + 2)(5x + 2).",
          hint2: "Expand: 25x² + 10x + 10x + 4.",
          hint3: "25x² + 20x + 4",
        },
      ],
    },
  },

  {
    id: "2.4",
    title: "Factorising",
    slug: "factorising",
    pageRef: "Pages 41-43",
    difficulty: "intermediate",
    learn: {
      explanation:
        "Factorising is the reverse of expanding brackets. Take out the highest common factor from every term.",
      keyPoints: [
        "Factorising is the opposite of expanding.",
        "Find the highest common factor of all terms.",
        "Take out the biggest common number and the highest common letter power.",
        "Check by expanding your answer.",
      ],
      workedExamples: [
        {
          problem: "Factorise 2x − 6",
          steps: [
            { step: 1, instruction: "Find the highest common factor", working: "HCF of 2x and 6 is 2." },
            { step: 2, instruction: "Put the HCF outside the bracket", working: "2(x − 3)" },
          ],
          answer: "2(x − 3)",
        },
        {
          problem: "Factorise 4x² + 8x",
          steps: [
            { step: 1, instruction: "Find the highest common factor", working: "HCF of 4x² and 8x is 4x." },
            { step: 2, instruction: "Divide each term by 4x", working: "4x(x + 2)" },
          ],
          answer: "4x(x + 2)",
        },
      ],
      whyItWorks:
        "Factorising rewrites an expression as a product. Expanding the brackets again should return the original expression.",
    },
    practice: {
      level1: [
        {
          id: "2.4.1",
          type: "text",
          question: "Factorise 10x + 25y",
          answer: "5(2x + 5y)",
          acceptedAnswers: ["5(2x + 5y)", "5(2x+5y)"],
          hint1: "The HCF of 10 and 25 is 5.",
          hint2: "10x ÷ 5 = 2x and 25y ÷ 5 = 5y.",
          hint3: "5(2x + 5y)",
        },
        {
          id: "2.4.2",
          type: "text",
          question: "Factorise y² − 2y",
          answer: "y(y - 2)",
          acceptedAnswers: ["y(y - 2)", "y(y-2)"],
          hint1: "Both terms contain y.",
          hint2: "y² ÷ y = y and 2y ÷ y = 2.",
          hint3: "y(y − 2)",
        },
      ],
      level2: [
        {
          id: "2.4.3",
          type: "text",
          question: "Factorise 6x² + 9x",
          answer: "3x(2x + 3)",
          acceptedAnswers: ["3x(2x + 3)", "3x(2x+3)"],
          hint1: "The HCF of 6x² and 9x is 3x.",
          hint2: "6x² ÷ 3x = 2x and 9x ÷ 3x = 3.",
          hint3: "3x(2x + 3)",
        },
        {
          id: "2.4.4",
          type: "choice",
          question: "Which is the complete factorisation of 36b + 12?",
          options: ["6(6b + 2)", "12(3b + 1)", "4(9b + 3)", "3(12b + 4)"],
          answer: "12(3b + 1)",
          hint1: "The HCF of 36 and 12 is 12.",
          hint2: "Complete factorisation uses the highest common factor.",
          hint3: "12(3b + 1)",
        },
      ],
    },
  },

  {
    id: "2.5",
    title: "Solving Equations",
    slug: "solving-equations",
    pageRef: "Pages 44-45",
    difficulty: "foundation",
    learn: {
      explanation:
        "To solve an equation, get the letter on its own by doing inverse operations to both sides.",
      keyPoints: [
        "Whatever you do to one side, do the same to the other.",
        "Use inverse operations to undo what has happened to the unknown.",
        "If the unknown appears on both sides, collect unknown terms on one side first.",
        "If there are brackets, expand them first unless there is a quicker equivalent method.",
      ],
      workedExamples: [
        {
          problem: "Solve 5x + 2 = 12",
          image: PAGE_IMAGES.p44_45,
          steps: [
            { step: 1, instruction: "Subtract 2 from both sides", working: "5x = 10" },
            { step: 2, instruction: "Divide both sides by 5", working: "x = 2" },
          ],
          answer: "x = 2",
        },
        {
          problem: "Solve 5x − 7 = 3x + 3",
          steps: [
            { step: 1, instruction: "Subtract 3x from both sides", working: "2x − 7 = 3" },
            { step: 2, instruction: "Add 7 to both sides", working: "2x = 10" },
            { step: 3, instruction: "Divide by 2", working: "x = 5" },
          ],
          answer: "x = 5",
        },
        {
          problem: "Solve 3(x − 1) = 2(x + 2)",
          steps: [
            { step: 1, instruction: "Expand both brackets", working: "3x − 3 = 2x + 4" },
            { step: 2, instruction: "Subtract 2x from both sides", working: "x − 3 = 4" },
            { step: 3, instruction: "Add 3 to both sides", working: "x = 7" },
          ],
          answer: "x = 7",
        },
      ],
      whyItWorks:
        "An equation is like a balanced scale. Doing the same operation to both sides keeps it balanced.",
    },
    practice: {
      warmUp: [
        {
          id: "2.5.w1",
          type: "text",
          question: "Solve x + 3 = 7",
          answer: "4",
          acceptedAnswers: ["4", "x=4", "x = 4"],
          hint1: "Subtract 3 from both sides.",
          hint2: "x = 7 − 3.",
          hint3: "x = 4",
        },
        {
          id: "2.5.w2",
          type: "text",
          question: "Solve 2x = 10",
          answer: "5",
          acceptedAnswers: ["5", "x=5", "x = 5"],
          hint1: "Divide both sides by 2.",
          hint2: "x = 10 ÷ 2.",
          hint3: "x = 5",
        },
      ],
      level1: [
        {
          id: "2.5.1",
          type: "text",
          question: "Solve 7k − 3 = 11",
          answer: "2",
          acceptedAnswers: ["2", "k=2", "k = 2"],
          hint1: "Add 3 to both sides: 7k = 14.",
          hint2: "Divide both sides by 7.",
          hint3: "k = 2",
        },
        {
          id: "2.5.2",
          type: "text",
          question: "Solve 3t + 6 = t + 12",
          answer: "3",
          acceptedAnswers: ["3", "t=3", "t = 3"],
          hint1: "Subtract t from both sides: 2t + 6 = 12.",
          hint2: "Subtract 6: 2t = 6.",
          hint3: "t = 3",
        },
      ],
      level2: [
        {
          id: "2.5.3",
          type: "text",
          question: "Solve (x + 3)/2 = x − 7",
          answer: "17",
          acceptedAnswers: ["17", "x=17", "x = 17"],
          hint1: "Multiply both sides by 2: x + 3 = 2x − 14.",
          hint2: "Subtract x: 3 = x − 14.",
          hint3: "x = 17",
        },
        {
          id: "2.5.4",
          type: "text",
          question: "Solve 3(x − 1) = 2(x + 2)",
          answer: "7",
          acceptedAnswers: ["7", "x=7", "x = 7"],
          hint1: "Expand: 3x − 3 = 2x + 4.",
          hint2: "Subtract 2x: x − 3 = 4.",
          hint3: "x = 7",
        },
      ],
      level3: [
        {
          id: "2.5.5",
          type: "text",
          question: "Solve (2x + 3)² = 81. Give both solutions, smallest first, separated by a comma.",
          answer: "-6, 3",
          acceptedAnswers: [
            "-6, 3",
            "-6,3",
            "x = -6, x = 3",
            "x=-6,x=3",
            "x = -6 or x = 3",
            "x=-6 or x=3",
            "3, -6",
            "3,-6",
            "x = 3 or x = -6",
            "x=3 or x=-6",
          ],
          hint1: "Square root both sides: 2x + 3 = ±9.",
          hint2: "Solve 2x + 3 = 9 and 2x + 3 = −9 separately.",
          hint3: "x = 3 or x = −6, so smallest first: −6, 3.",
        },
      ],
    },
  },

  {
    id: "2.6",
    title: "Using & Making Formulae",
    slug: "formulas",
    pageRef: "Pages 46-49",
    difficulty: "intermediate",
    learn: {
      explanation:
        "A formula is a rule linking variables. You can substitute values into a formula or rearrange it to change the subject.",
      keyPoints: [
        "Substitute means replace letters with numbers.",
        "An expression has no equals sign, for example 3x + 2.",
        "An equation has an equals sign and can be solved, for example 3x + 2 = 11.",
        "A formula links variables, for example A = πr².",
        "Rearranging formulae uses the same balance rules as solving equations.",
      ],
      workedExamples: [
        {
          problem: "If L = 3j − 2k, find L when j = 4 and k = 5",
          image: PAGE_IMAGES.p46_47,
          steps: [
            { step: 1, instruction: "Substitute values", working: "L = 3(4) − 2(5)" },
            { step: 2, instruction: "Calculate", working: "L = 12 − 10 = 2" },
          ],
          answer: "L = 2",
        },
        {
          problem: "Rearrange a = 2b − 3 to make b the subject",
          steps: [
            { step: 1, instruction: "Add 3 to both sides", working: "a + 3 = 2b" },
            { step: 2, instruction: "Divide both sides by 2", working: "b = (a + 3)/2" },
          ],
          answer: "b = (a + 3)/2",
        },
      ],
      whyItWorks:
        "Formulae model relationships. Rearranging lets you calculate whichever variable you need.",
    },
    practice: {
      warmUp: [
        {
          id: "2.6.w1",
          type: "text",
          question: "If a = 3, b = 2 and c = 5, find the value of 2a + 3b − c.",
          answer: "7",
          acceptedAnswers: ["7"],
          hint1: "Substitute: 2(3) + 3(2) − 5.",
          hint2: "6 + 6 − 5.",
          hint3: "7",
        },
      ],
      level1: [
        {
          id: "2.6.1",
          type: "text",
          question: "C = 50r + 100. Find C when r = 7.",
          answer: "450",
          acceptedAnswers: ["450", "C=450", "C = 450"],
          hint1: "C = 50(7) + 100.",
          hint2: "C = 350 + 100.",
          hint3: "C = 450",
        },
        {
          id: "2.6.2",
          type: "text",
          question: "C = 50r + 100. Find r when C = 450.",
          answer: "7",
          acceptedAnswers: ["7", "r=7", "r = 7"],
          hint1: "450 = 50r + 100.",
          hint2: "350 = 50r.",
          hint3: "r = 7",
        },
      ],
      level2: [
        {
          id: "2.6.3",
          type: "text",
          question: "Rearrange p = 5 + 3w to make w the subject.",
          answer: "w = (p - 5)/3",
          acceptedAnswers: ["w = (p - 5)/3", "w=(p-5)/3", "(p-5)/3"],
          hint1: "Subtract 5: p − 5 = 3w.",
          hint2: "Divide by 3.",
          hint3: "w = (p − 5)/3",
        },
        {
          id: "2.6.4",
          type: "text",
          question: "Make d the subject of a = (2b − d)/3.",
          answer: "d = 2b - 3a",
          acceptedAnswers: ["d = 2b - 3a", "d=2b-3a", "2b - 3a", "2b-3a"],
          hint1: "Multiply by 3: 3a = 2b − d.",
          hint2: "Add d to both sides: 3a + d = 2b.",
          hint3: "d = 2b − 3a",
        },
      ],
    },
  },

  {
    id: "2.7",
    title: "Sequences",
    slug: "sequences",
    pageRef: "Pages 50-53",
    difficulty: "foundation",
    learn: {
      explanation:
        "A sequence is a list of numbers that follows a pattern. Arithmetic sequences have a common difference. Geometric sequences have a common ratio.",
      keyPoints: [
        "Arithmetic sequences add or subtract the same number each time.",
        "Geometric sequences multiply or divide by the same number each time.",
        "The nth term gives a formula for any term in the sequence.",
        "For an arithmetic sequence, find the common difference first.",
      ],
      workedExamples: [
        {
          problem: "Find the nth term of 2, 8, 14, 20, ...",
          image: PAGE_IMAGES.p52_53,
          steps: [
            { step: 1, instruction: "Find the common difference", working: "8 − 2 = 6, 14 − 8 = 6, so d = 6." },
            { step: 2, instruction: "Start with 6n", working: "6n gives 6, 12, 18, 24, ..." },
            { step: 3, instruction: "Compare with the sequence", working: "2, 8, 14, 20 are each 4 less than 6n." },
          ],
          answer: "6n − 4",
        },
        {
          problem: "Is 29 in the sequence 3n + 4?",
          steps: [
            { step: 1, instruction: "Set the nth term equal to 29", working: "3n + 4 = 29" },
            { step: 2, instruction: "Solve", working: "3n = 25, so n = 25/3" },
            { step: 3, instruction: "Check if n is a whole number", working: "25/3 is not a whole number." },
          ],
          answer: "No — 29 is not in the sequence.",
        },
      ],
      whyItWorks:
        "The nth term formula lets you find any term without listing every term before it.",
    },
    practice: {
      warmUp: [
        {
          id: "2.7.w1",
          type: "text",
          question: "Find the next two terms: 2, 5, 8, 11, ...",
          answer: "14, 17",
          acceptedAnswers: ["14, 17", "14,17"],
          hint1: "The common difference is 3.",
          hint2: "11 + 3 = 14.",
          hint3: "14 + 3 = 17.",
        },
        {
          id: "2.7.w2",
          type: "text",
          question: "Find the next two terms: 4, 12, 36, ...",
          answer: "108, 324",
          acceptedAnswers: ["108, 324", "108,324"],
          hint1: "The common ratio is 3.",
          hint2: "36 × 3 = 108.",
          hint3: "108 × 3 = 324.",
        },
      ],
      level1: [
        {
          id: "2.7.1",
          type: "text",
          question: "Find the nth term of 7, 9, 11, 13, ...",
          answer: "2n + 5",
          acceptedAnswers: ["2n + 5", "2n+5"],
          hint1: "The common difference is 2.",
          hint2: "2n gives 2, 4, 6, 8, ...",
          hint3: "The sequence is 5 more, so 2n + 5.",
        },
      ],
      level2: [
        {
          id: "2.7.2",
          type: "text",
          question: "Find the nth term of 11, 7, 3, −1, ...",
          answer: "-4n + 15",
          acceptedAnswers: ["-4n + 15", "-4n+15", "15 - 4n", "15-4n"],
          hint1: "The common difference is −4.",
          hint2: "−4n gives −4, −8, −12, −16, ...",
          hint3: "The sequence is 15 more, so −4n + 15.",
        },
        {
          id: "2.7.3",
          type: "choice",
          question: "Is 38 in the sequence 5n + 2?",
          options: ["Yes", "No"],
          answer: "No",
          hint1: "Set 5n + 2 = 38.",
          hint2: "5n = 36.",
          hint3: "n = 7.2, which is not a whole number, so No.",
        },
      ],
    },
  },

  {
    id: "2.8",
    title: "Inequalities",
    slug: "inequalities",
    pageRef: "Pages 52-55",
    difficulty: "foundation",
    learn: {
      explanation:
        "Inequalities use >, <, ≥ or ≤ instead of =. They describe a range of possible values.",
      keyPoints: [
        "≤ and ≥ include the endpoint.",
        "< and > do not include the endpoint.",
        "On a number line, a closed circle means included.",
        "On a number line, an open circle means not included.",
        "When listing integers, include only whole-number values that satisfy the inequality.",
      ],
      workedExamples: [
        {
          problem: "List all integers where −2 < x ≤ 4",
          image: PAGE_IMAGES.p52_53,
          steps: [
            { step: 1, instruction: "Understand the lower bound", working: "x must be greater than −2, so −2 is not included." },
            { step: 2, instruction: "Understand the upper bound", working: "x must be less than or equal to 4, so 4 is included." },
            { step: 3, instruction: "List the integers", working: "−1, 0, 1, 2, 3, 4" },
          ],
          answer: "−1, 0, 1, 2, 3, 4",
        },
      ],
      whyItWorks:
        "Inequalities describe intervals or ranges, while equations usually give exact values.",
    },
    practice: {
      level1: [
        {
          id: "2.8.1",
          type: "text",
          question: "List the positive integers where x ≤ 3.",
          answer: "1, 2, 3",
          acceptedAnswers: ["1, 2, 3", "1,2,3"],
          hint1: "x can equal 3 because ≤ includes 3.",
          hint2: "Positive integers start at 1.",
          hint3: "1, 2, 3",
        },
        {
          id: "2.8.2",
          type: "text",
          question: "List the integers where −3 ≤ n < 3.",
          answer: "-3, -2, -1, 0, 1, 2",
          acceptedAnswers: ["-3, -2, -1, 0, 1, 2", "-3,-2,-1,0,1,2"],
          hint1: "−3 is included.",
          hint2: "3 is not included.",
          hint3: "−3, −2, −1, 0, 1, 2",
        },
      ],
      level2: [
        {
          id: "2.8.3",
          type: "text",
          question: "Describe how to show 1 < x ≤ 7 on a number line.",
          answer: "open circle at 1, closed circle at 7, line between",
          acceptedAnswers: [
            "open circle at 1, closed circle at 7, line between",
            "open circle on 1, closed circle on 7, line between",
            "open at 1 closed at 7 line between",
          ],
          hint1: "< means open circle at 1.",
          hint2: "≤ means closed circle at 7.",
          hint3: "Draw a line between 1 and 7.",
        },
      ],
    },
  },

  {
    id: "2.9",
    title: "Factorising x² + bx + c",
    slug: "factorising-monic-quadratic",
    pageRef: "Phase 3 expansion",
    difficulty: "higher",
    learn: {
      explanation:
        "When the coefficient of x² is 1, factorising x² + bx + c means finding two numbers that multiply to c and add to b.",
      keyPoints: [
        "Find two numbers that multiply to c and add to b.",
        "If c is positive, the two numbers have the same sign.",
        "If c is negative, the two numbers have opposite signs.",
        "Always check by expanding back out.",
        "If there is no integer pair, the quadratic may need the formula.",
      ],
      workedExamples: [
        {
          problem: "Factorise x² + 7x + 12",
          steps: [
            { step: 1, instruction: "Find two numbers", working: "They must multiply to 12 and add to 7." },
            { step: 2, instruction: "Choose the pair", working: "3 and 4 work because 3 × 4 = 12 and 3 + 4 = 7." },
            { step: 3, instruction: "Write the brackets", working: "(x + 3)(x + 4)" },
          ],
          answer: "(x + 3)(x + 4)",
        },
        {
          problem: "Factorise x² − x − 12",
          steps: [
            { step: 1, instruction: "Notice c is negative", working: "The signs must be opposite." },
            { step: 2, instruction: "Find the pair", working: "−4 and 3 multiply to −12 and add to −1." },
            { step: 3, instruction: "Write the brackets", working: "(x − 4)(x + 3)" },
          ],
          answer: "(x − 4)(x + 3)",
        },
      ],
      whyItWorks:
        "Expanding (x + p)(x + q) gives x² + (p + q)x + pq. So p + q is the x coefficient and pq is the constant.",
    },
    practice: {
      warmUp: [
        {
          id: "2.9.w1",
          type: "text",
          question: "Find two numbers that multiply to 6 and add to 5.",
          answer: "2, 3",
          acceptedAnswers: ["2, 3", "2,3", "3, 2", "3,2"],
          hint1: "Try factor pairs of 6.",
          hint2: "1 and 6 add to 7. 2 and 3 add to 5.",
          hint3: "2 and 3",
        },
        {
          id: "2.9.w2",
          type: "text",
          question: "Factorise x² + 5x + 6.",
          answer: "(x+2)(x+3)",
          acceptedAnswers: ["(x+2)(x+3)", "(x + 2)(x + 3)", "(x+3)(x+2)", "(x + 3)(x + 2)"],
          hint1: "Find two numbers that multiply to 6 and add to 5.",
          hint2: "The numbers are 2 and 3.",
          hint3: "(x + 2)(x + 3)",
        },
      ],
      level1: [
        {
          id: "2.9.1",
          type: "text",
          question: "Factorise x² + 9x + 20.",
          answer: "(x+4)(x+5)",
          acceptedAnswers: ["(x+4)(x+5)", "(x + 4)(x + 5)", "(x+5)(x+4)", "(x + 5)(x + 4)"],
          hint1: "Find two numbers that multiply to 20 and add to 9.",
          hint2: "4 and 5.",
          hint3: "(x + 4)(x + 5)",
        },
        {
          id: "2.9.2",
          type: "text",
          question: "Factorise x² − 5x + 6.",
          answer: "(x-2)(x-3)",
          acceptedAnswers: ["(x-2)(x-3)", "(x - 2)(x - 3)", "(x-3)(x-2)", "(x - 3)(x - 2)"],
          hint1: "Find two numbers that multiply to 6 and add to −5.",
          hint2: "Both numbers must be negative: −2 and −3.",
          hint3: "(x − 2)(x − 3)",
        },
      ],
      level2: [
        {
          id: "2.9.3",
          type: "text",
          question: "Factorise x² + 2x − 15.",
          answer: "(x+5)(x-3)",
          acceptedAnswers: ["(x+5)(x-3)", "(x + 5)(x - 3)", "(x-3)(x+5)", "(x - 3)(x + 5)"],
          hint1: "c is negative, so the signs are opposite.",
          hint2: "5 and −3 multiply to −15 and add to 2.",
          hint3: "(x + 5)(x − 3)",
        },
        {
          id: "2.9.4",
          type: "text",
          question: "Factorise x² − x − 20.",
          answer: "(x-5)(x+4)",
          acceptedAnswers: ["(x-5)(x+4)", "(x - 5)(x + 4)", "(x+4)(x-5)", "(x + 4)(x - 5)"],
          hint1: "Find two numbers that multiply to −20 and add to −1.",
          hint2: "−5 and 4.",
          hint3: "(x − 5)(x + 4)",
        },
      ],
      level3: [
        {
          id: "2.9.5",
          type: "text",
          question: "Factorise x² − 14x + 49.",
          answer: "(x-7)^2",
          acceptedAnswers: ["(x-7)^2", "(x - 7)^2", "(x-7)²", "(x - 7)²", "(x-7)(x-7)", "(x - 7)(x - 7)"],
          hint1: "Find two numbers that multiply to 49 and add to −14.",
          hint2: "−7 and −7.",
          hint3: "(x − 7)²",
        },
      ],
    },
    aiChallenge: {
      prompt:
        "A pupil writes: 'x² + 5x − 6 factorises to (x + 2)(x + 3)'. Explain two things wrong with this answer and give the correct factorisation.",
      successCriteria: [
        "Identifies that (x + 2)(x + 3) expands to x² + 5x + 6, not x² + 5x − 6.",
        "Identifies that the pupil ignored the negative sign on −6.",
        "Explains that the correct pair must multiply to −6 and add to 5.",
        "Gives the correct answer: (x + 6)(x − 1).",
      ],
    },
  },

  {
    id: "2.10",
    title: "Difference of Two Squares",
    slug: "difference-two-squares",
    pageRef: "Phase 3 expansion",
    difficulty: "higher",
    learn: {
      explanation:
        "A difference of two squares has the form a² − b² and factorises to (a − b)(a + b).",
      keyPoints: [
        "a² − b² = (a − b)(a + b).",
        "Look for two perfect squares with a minus sign between them.",
        "x² − 9 = (x − 3)(x + 3).",
        "4y² − 25 = (2y − 5)(2y + 5).",
        "a² + b² is not a difference of two squares.",
      ],
      workedExamples: [
        {
          problem: "Factorise x² − 49",
          steps: [
            { step: 1, instruction: "Identify the squares", working: "x² and 49 = 7²." },
            { step: 2, instruction: "Apply the pattern", working: "x² − 7² = (x − 7)(x + 7)." },
          ],
          answer: "(x − 7)(x + 7)",
        },
        {
          problem: "Factorise 9x² − 16y²",
          steps: [
            { step: 1, instruction: "Identify the square roots", working: "9x² = (3x)² and 16y² = (4y)²." },
            { step: 2, instruction: "Apply the pattern", working: "(3x − 4y)(3x + 4y)" },
          ],
          answer: "(3x − 4y)(3x + 4y)",
        },
      ],
      whyItWorks:
        "When you expand (a − b)(a + b), the middle terms cancel, leaving a² − b².",
    },
    practice: {
      warmUp: [
        {
          id: "2.10.w1",
          type: "text",
          question: "Factorise x² − 4.",
          answer: "(x-2)(x+2)",
          acceptedAnswers: ["(x-2)(x+2)", "(x - 2)(x + 2)", "(x+2)(x-2)", "(x + 2)(x - 2)"],
          hint1: "4 = 2².",
          hint2: "Use a² − b² = (a − b)(a + b).",
          hint3: "(x − 2)(x + 2)",
        },
        {
          id: "2.10.w2",
          type: "text",
          question: "Factorise y² − 1.",
          answer: "(y-1)(y+1)",
          acceptedAnswers: ["(y-1)(y+1)", "(y - 1)(y + 1)", "(y+1)(y-1)", "(y + 1)(y - 1)"],
          hint1: "1 = 1².",
          hint2: "Use the difference of two squares pattern.",
          hint3: "(y − 1)(y + 1)",
        },
      ],
      level1: [
        {
          id: "2.10.1",
          type: "text",
          question: "Factorise x² − 81.",
          answer: "(x-9)(x+9)",
          acceptedAnswers: ["(x-9)(x+9)", "(x - 9)(x + 9)", "(x+9)(x-9)", "(x + 9)(x - 9)"],
          hint1: "81 = 9².",
          hint2: "Use a² − b².",
          hint3: "(x − 9)(x + 9)",
        },
        {
          id: "2.10.2",
          type: "text",
          question: "Factorise 4x² − 9.",
          answer: "(2x-3)(2x+3)",
          acceptedAnswers: ["(2x-3)(2x+3)", "(2x - 3)(2x + 3)", "(2x+3)(2x-3)", "(2x + 3)(2x - 3)"],
          hint1: "4x² = (2x)².",
          hint2: "9 = 3².",
          hint3: "(2x − 3)(2x + 3)",
        },
      ],
      level2: [
        {
          id: "2.10.3",
          type: "text",
          question: "Solve x² − 16 = 0. Give your answers smallest first, separated by a comma.",
          answer: "-4, 4",
          acceptedAnswers: ["-4, 4", "-4,4", "x=-4, x=4", "x = -4, x = 4", "x = -4 or x = 4"],
          hint1: "Factorise x² − 16.",
          hint2: "(x − 4)(x + 4) = 0.",
          hint3: "x = −4 or x = 4.",
        },
        {
          id: "2.10.4",
          type: "text",
          question: "Factorise 25 − a².",
          answer: "(5-a)(5+a)",
          acceptedAnswers: ["(5-a)(5+a)", "(5 - a)(5 + a)", "(5+a)(5-a)", "(5 + a)(5 - a)"],
          hint1: "25 = 5².",
          hint2: "Use a² − b², with 5 first.",
          hint3: "(5 − a)(5 + a)",
        },
      ],
      level3: [
        {
          id: "2.10.5",
          type: "text",
          question: "Factorise fully x⁴ − 16.",
          answer: "(x-2)(x+2)(x^2+4)",
          acceptedAnswers: [
            "(x-2)(x+2)(x^2+4)",
            "(x - 2)(x + 2)(x^2 + 4)",
            "(x-2)(x+2)(x²+4)",
            "(x - 2)(x + 2)(x² + 4)",
            "(x+2)(x-2)(x^2+4)",
          ],
          hint1: "First use difference of two squares: x⁴ − 16 = (x² − 4)(x² + 4).",
          hint2: "Then factorise x² − 4.",
          hint3: "(x − 2)(x + 2)(x² + 4)",
        },
      ],
    },
    aiChallenge: {
      prompt:
        "A pupil claims: 'x² + 9 factorises as (x + 3)(x + 3) using difference of squares.' Explain why both parts of this claim are wrong.",
      successCriteria: [
        "Identifies that x² + 9 is a sum of squares, not a difference of squares.",
        "States that x² + 9 cannot be factorised over the real numbers.",
        "Identifies that (x + 3)(x + 3) expands to x² + 6x + 9, not x² + 9.",
        "Explains the difference between (a + b)(a + b) and (a − b)(a + b).",
      ],
    },
  },

  {
    id: "2.11",
    title: "Factorising ax² + bx + c",
    slug: "factorising-non-monic",
    pageRef: "Phase 3 expansion — Higher only",
    difficulty: "higher",
    learn: {
      explanation:
        "When the coefficient of x² is not 1, use the AC method: multiply a × c, find a pair that multiplies to ac and adds to b, split the middle term, then factorise by grouping.",
      keyPoints: [
        "Step 1: Find ac by multiplying the first and last coefficients.",
        "Step 2: Find two numbers that multiply to ac and add to b.",
        "Step 3: Split the middle term using that pair.",
        "Step 4: Factorise by grouping.",
        "Step 5: Check by expanding back.",
      ],
      workedExamples: [
        {
          problem: "Factorise 2x² + 7x + 3",
          steps: [
            { step: 1, instruction: "Find ac", working: "2 × 3 = 6." },
            { step: 2, instruction: "Find the pair", working: "1 and 6 multiply to 6 and add to 7." },
            { step: 3, instruction: "Split the middle term", working: "2x² + 6x + x + 3." },
            { step: 4, instruction: "Group", working: "2x(x + 3) + 1(x + 3) = (2x + 1)(x + 3)." },
          ],
          answer: "(2x + 1)(x + 3)",
        },
        {
          problem: "Factorise 6x² − x − 2",
          steps: [
            { step: 1, instruction: "Find ac", working: "6 × −2 = −12." },
            { step: 2, instruction: "Find the pair", working: "−4 and 3 multiply to −12 and add to −1." },
            { step: 3, instruction: "Split the middle term", working: "6x² − 4x + 3x − 2." },
            { step: 4, instruction: "Group", working: "2x(3x − 2) + 1(3x − 2) = (2x + 1)(3x − 2)." },
          ],
          answer: "(2x + 1)(3x − 2)",
        },
      ],
      whyItWorks:
        "Splitting the middle term using the correct AC pair creates two groups with a shared bracket.",
    },
    practice: {
      warmUp: [
        {
          id: "2.11.w1",
          type: "text",
          question: "Find ac for 3x² + 5x + 2.",
          answer: "6",
          acceptedAnswers: ["6"],
          hint1: "Multiply the coefficient of x² by the constant.",
          hint2: "3 × 2.",
          hint3: "6",
        },
        {
          id: "2.11.w2",
          type: "text",
          question: "Find two numbers that multiply to 6 and add to 5.",
          answer: "2, 3",
          acceptedAnswers: ["2, 3", "2,3", "3, 2", "3,2"],
          hint1: "Try factor pairs of 6.",
          hint2: "2 × 3 = 6 and 2 + 3 = 5.",
          hint3: "2 and 3",
        },
      ],
      level1: [
        {
          id: "2.11.1",
          type: "text",
          question: "Factorise 3x² + 5x + 2.",
          answer: "(3x+2)(x+1)",
          acceptedAnswers: ["(3x+2)(x+1)", "(3x + 2)(x + 1)", "(x+1)(3x+2)", "(x + 1)(3x + 2)"],
          hint1: "ac = 6, pair 2 and 3.",
          hint2: "Split: 3x² + 2x + 3x + 2.",
          hint3: "x(3x + 2) + 1(3x + 2) = (3x + 2)(x + 1).",
        },
      ],
      level2: [
        {
          id: "2.11.2",
          type: "text",
          question: "Factorise 2x² + 5x − 3.",
          answer: "(2x-1)(x+3)",
          acceptedAnswers: ["(2x-1)(x+3)", "(2x - 1)(x + 3)", "(x+3)(2x-1)", "(x + 3)(2x - 1)"],
          hint1: "ac = −6. Find a pair that multiplies to −6 and adds to 5.",
          hint2: "The pair is −1 and 6.",
          hint3: "2x² − x + 6x − 3 = (2x − 1)(x + 3).",
        },
        {
          id: "2.11.3",
          type: "text",
          question: "Factorise 3x² − 11x − 4.",
          answer: "(3x+1)(x-4)",
          acceptedAnswers: ["(3x+1)(x-4)", "(3x + 1)(x - 4)", "(x-4)(3x+1)", "(x - 4)(3x + 1)"],
          hint1: "ac = −12. Find a pair that multiplies to −12 and adds to −11.",
          hint2: "The pair is −12 and 1.",
          hint3: "3x² − 12x + x − 4 = (3x + 1)(x − 4).",
        },
      ],
      level3: [
        {
          id: "2.11.4",
          type: "text",
          question: "Solve 2x² + 7x + 3 = 0. Give your answers smallest first.",
          answer: "-3, -1/2",
          acceptedAnswers: ["-3, -1/2", "-3,-1/2", "-3, -0.5", "-3,-0.5", "x=-3, x=-1/2", "x = -3 or x = -1/2"],
          hint1: "Factorise: (2x + 1)(x + 3) = 0.",
          hint2: "Set each bracket equal to 0.",
          hint3: "x = −3 or x = −1/2.",
        },
      ],
    },
    aiChallenge: {
      prompt:
        "Use the AC method to factorise 6x² + 11x − 10. Show every step including ac, the pair, the split and the grouping.",
      successCriteria: [
        "States ac = 6 × −10 = −60.",
        "Finds the pair 15 and −4.",
        "Splits the middle term: 6x² + 15x − 4x − 10.",
        "Groups correctly: 3x(2x + 5) − 2(2x + 5).",
        "Gives final answer: (3x − 2)(2x + 5).",
      ],
    },
  },

  {
    id: "2.12",
    title: "Solving Quadratics by Factorising",
    slug: "quadratics-factorising",
    pageRef: "Phase 3 — GCSE expansion",
    difficulty: "higher",
    learn: {
      explanation:
        "A quadratic equation has the form ax² + bx + c = 0. To solve by factorising, write the left side as a product of brackets, then set each bracket equal to zero.",
      keyPoints: [
        "Always rearrange so one side equals 0 before factorising.",
        "For x² + bx + c, find two numbers that multiply to c and add to b.",
        "Each bracket equal to zero gives one solution.",
        "A quadratic can have two, one or no real solutions.",
        "Check by substituting both answers back into the original equation.",
      ],
      workedExamples: [
        {
          problem: "Solve x² + 5x + 6 = 0",
          steps: [
            { step: 1, instruction: "Find two numbers", working: "They must multiply to 6 and add to 5: 2 and 3." },
            { step: 2, instruction: "Factorise", working: "(x + 2)(x + 3) = 0." },
            { step: 3, instruction: "Solve each bracket", working: "x + 2 = 0 gives x = −2. x + 3 = 0 gives x = −3." },
          ],
          answer: "x = −2 or x = −3",
        },
        {
          problem: "Solve x² − 7x + 12 = 0",
          steps: [
            { step: 1, instruction: "Find two numbers", working: "They must multiply to 12 and add to −7: −3 and −4." },
            { step: 2, instruction: "Factorise", working: "(x − 3)(x − 4) = 0." },
            { step: 3, instruction: "Solve", working: "x = 3 or x = 4." },
          ],
          answer: "x = 3 or x = 4",
        },
        {
          problem: "Solve x² = 5x",
          steps: [
            { step: 1, instruction: "Rearrange to equal zero", working: "x² − 5x = 0." },
            { step: 2, instruction: "Factorise", working: "x(x − 5) = 0." },
            { step: 3, instruction: "Solve", working: "x = 0 or x = 5." },
          ],
          answer: "x = 0 or x = 5",
        },
      ],
      whyItWorks:
        "If two factors multiply to make zero, at least one of the factors must be zero.",
    },
    practice: {
      warmUp: [
        {
          id: "2.12.w1",
          type: "text",
          question: "Factorise x² + 6x + 8.",
          answer: "(x+2)(x+4)",
          acceptedAnswers: ["(x+2)(x+4)", "(x + 2)(x + 4)", "(x+4)(x+2)", "(x + 4)(x + 2)"],
          hint1: "Find two numbers that multiply to 8 and add to 6.",
          hint2: "2 and 4.",
          hint3: "(x + 2)(x + 4)",
        },
        {
          id: "2.12.w2",
          type: "text",
          question: "Solve (x − 1)(x + 5) = 0. Give both answers.",
          answer: "1, -5",
          acceptedAnswers: ["1, -5", "1,-5", "-5, 1", "-5,1", "x=1, x=-5", "x = 1 or x = -5"],
          hint1: "Set each bracket equal to zero.",
          hint2: "x − 1 = 0 or x + 5 = 0.",
          hint3: "x = 1 or x = −5.",
        },
      ],
      level1: [
        {
          id: "2.12.1",
          type: "text",
          question: "Solve x² + 8x + 15 = 0. Give the two solutions, smallest first.",
          answer: "-5, -3",
          acceptedAnswers: ["-5, -3", "-5,-3", "x=-5, x=-3", "x = -5 or x = -3"],
          hint1: "Find two numbers that multiply to 15 and add to 8.",
          hint2: "3 and 5.",
          hint3: "(x + 3)(x + 5) = 0, so x = −3 or x = −5.",
        },
        {
          id: "2.12.2",
          type: "text",
          question: "Solve x² − 9 = 0. Give the answers smallest first.",
          answer: "-3, 3",
          acceptedAnswers: ["-3, 3", "-3,3", "x=-3, x=3", "x = -3 or x = 3"],
          hint1: "Use difference of two squares: x² − 9 = (x − 3)(x + 3).",
          hint2: "Set each bracket equal to zero.",
          hint3: "x = −3 or x = 3.",
        },
      ],
      level2: [
        {
          id: "2.12.3",
          type: "text",
          question: "Solve x² − 5x − 14 = 0. Give the answers smallest first.",
          answer: "-2, 7",
          acceptedAnswers: ["-2, 7", "-2,7", "x=-2, x=7", "x = -2 or x = 7"],
          hint1: "Find two numbers that multiply to −14 and add to −5.",
          hint2: "−7 and 2.",
          hint3: "(x − 7)(x + 2) = 0.",
        },
        {
          id: "2.12.4",
          type: "text",
          question: "Solve x² = 4x + 21. Give the answers smallest first.",
          answer: "-3, 7",
          acceptedAnswers: ["-3, 7", "-3,7", "x=-3, x=7", "x = -3 or x = 7"],
          hint1: "Rearrange: x² − 4x − 21 = 0.",
          hint2: "Factorise: (x − 7)(x + 3) = 0.",
          hint3: "x = −3 or x = 7.",
        },
      ],
      level3: [
        {
          id: "2.12.5",
          type: "text",
          question: "Solve 2x² + 7x + 3 = 0. Give the answers smallest first.",
          answer: "-3, -1/2",
          acceptedAnswers: ["-3, -1/2", "-3,-1/2", "-3, -0.5", "-3,-0.5", "x=-3, x=-1/2", "x = -3 or x = -1/2"],
          hint1: "Use the AC method: 2 × 3 = 6; pair 1 and 6.",
          hint2: "2x² + 6x + x + 3 = (2x + 1)(x + 3).",
          hint3: "x = −3 or x = −1/2.",
        },
      ],
    },
    aiChallenge: {
      prompt:
        "A pupil writes: 'To solve x² + 4x = 0, divide both sides by x to get x + 4 = 0, so x = −4.' Explain two things wrong with this approach and give the correct full solution.",
      successCriteria: [
        "Identifies that dividing by x loses the solution x = 0.",
        "Identifies that you should not divide by an unknown that could be zero.",
        "Correctly factorises x(x + 4) = 0.",
        "Gives both solutions: x = 0 and x = −4.",
      ],
    },
  },

  {
    id: "2.13",
    title: "The Quadratic Formula",
    slug: "quadratic-formula",
    pageRef: "Phase 3 — GCSE expansion",
    difficulty: "higher",
    learn: {
      explanation:
        "The quadratic formula solves any quadratic in the form ax² + bx + c = 0. The formula is x = (−b ± √(b² − 4ac)) / 2a.",
      keyPoints: [
        "Rearrange to ax² + bx + c = 0 before identifying a, b and c.",
        "The signs of b and c matter.",
        "The discriminant is b² − 4ac.",
        "Positive discriminant means two real solutions.",
        "Zero discriminant means one repeated real solution.",
        "Negative discriminant means no real solutions.",
      ],
      workedExamples: [
        {
          problem: "Solve 2x² + 3x − 5 = 0 using the formula",
          steps: [
            { step: 1, instruction: "Identify a, b and c", working: "a = 2, b = 3, c = −5." },
            { step: 2, instruction: "Calculate the discriminant", working: "b² − 4ac = 9 − 4(2)(−5) = 49." },
            { step: 3, instruction: "Apply the formula", working: "x = (−3 ± √49) / 4 = (−3 ± 7) / 4." },
            { step: 4, instruction: "Find both answers", working: "x = 1 or x = −5/2." },
          ],
          answer: "x = 1 or x = −5/2",
        },
        {
          problem: "Solve x² − 4x + 1 = 0. Give exact answers.",
          steps: [
            { step: 1, instruction: "Identify a, b and c", working: "a = 1, b = −4, c = 1." },
            { step: 2, instruction: "Calculate the discriminant", working: "b² − 4ac = 16 − 4 = 12." },
            { step: 3, instruction: "Apply the formula", working: "x = (4 ± √12) / 2." },
            { step: 4, instruction: "Simplify the surd", working: "√12 = 2√3, so x = 2 ± √3." },
          ],
          answer: "x = 2 + √3 or x = 2 − √3",
        },
      ],
      whyItWorks:
        "The formula comes from completing the square on the general quadratic ax² + bx + c = 0.",
    },
    practice: {
      warmUp: [
        {
          id: "2.13.w1",
          type: "text",
          question: "For 3x² + 5x − 2 = 0, what are a, b and c? Use the format a,b,c.",
          answer: "3,5,-2",
          acceptedAnswers: ["3,5,-2", "3, 5, -2"],
          hint1: "Match each coefficient to ax² + bx + c.",
          hint2: "a is the coefficient of x².",
          hint3: "a = 3, b = 5, c = −2.",
        },
        {
          id: "2.13.w2",
          type: "text",
          question: "Find the discriminant for a = 1, b = 5 and c = 6.",
          answer: "1",
          acceptedAnswers: ["1"],
          hint1: "Use b² − 4ac.",
          hint2: "25 − 4(1)(6).",
          hint3: "25 − 24 = 1.",
        },
      ],
      level1: [
        {
          id: "2.13.1",
          type: "text",
          question: "Solve x² + 5x + 6 = 0 using the formula. Give the answers smallest first.",
          answer: "-3, -2",
          acceptedAnswers: ["-3, -2", "-3,-2", "x=-3, x=-2", "x = -3 or x = -2"],
          hint1: "a = 1, b = 5, c = 6. The discriminant is 1.",
          hint2: "x = (−5 ± 1) / 2.",
          hint3: "x = −3 or x = −2.",
        },
      ],
      level2: [
        {
          id: "2.13.2",
          type: "text",
          question: "Solve x² + 4x + 2 = 0. Give exact answers.",
          answer: "-2 + sqrt(2), -2 - sqrt(2)",
          acceptedAnswers: [
            "-2 + sqrt(2), -2 - sqrt(2)",
            "-2 - sqrt(2), -2 + sqrt(2)",
            "-2 + √2, -2 - √2",
            "-2 - √2, -2 + √2",
            "x = -2 ± √2",
            "-2 ± √2",
          ],
          hint1: "a = 1, b = 4, c = 2. The discriminant is 16 − 8 = 8.",
          hint2: "√8 = 2√2.",
          hint3: "x = −2 ± √2.",
        },
        {
          id: "2.13.3",
          type: "text",
          question: "Solve 3x² − 5x − 2 = 0. Give the answers smallest first.",
          answer: "-1/3, 2",
          acceptedAnswers: ["-1/3, 2", "-1/3,2", "-0.333, 2", "x=-1/3, x=2", "x = -1/3 or x = 2"],
          hint1: "a = 3, b = −5, c = −2. The discriminant is 25 + 24 = 49.",
          hint2: "x = (5 ± 7) / 6.",
          hint3: "x = 2 or x = −1/3.",
        },
      ],
      level3: [
        {
          id: "2.13.4",
          type: "text",
          question: "Solve 2x² + 3x − 4 = 0 to 3 significant figures. Give the answers smallest first.",
          answer: "-2.35, 0.851",
          acceptedAnswers: ["-2.35, 0.851", "-2.35,0.851", "-2.35, 0.85", "-2.35,0.85"],
          hint1: "The discriminant is 9 + 32 = 41.",
          hint2: "x = (−3 ± √41) / 4.",
          hint3: "x ≈ −2.35 or x ≈ 0.851.",
        },
        {
          id: "2.13.5",
          type: "text",
          question: "Find the discriminant of x² + x + 1.",
          answer: "-3",
          acceptedAnswers: ["-3"],
          hint1: "Use b² − 4ac.",
          hint2: "1² − 4(1)(1).",
          hint3: "−3, so there are no real solutions.",
        },
      ],
    },
    aiChallenge: {
      prompt:
        "Explain how the discriminant b² − 4ac tells us the number of real solutions of a quadratic. Give a worked example for each case: two solutions, one repeated solution and no real solutions.",
      successCriteria: [
        "States that a positive discriminant gives two real solutions.",
        "States that a zero discriminant gives one repeated real solution.",
        "States that a negative discriminant gives no real solutions.",
        "Gives a valid worked example for each case.",
        "Shows the discriminant calculation explicitly.",
      ],
    },
  },

  {
    id: "2.14",
    title: "Completing the Square",
    slug: "completing-the-square",
    pageRef: "Phase 3 — GCSE expansion — Higher only",
    difficulty: "higher",
    learn: {
      explanation:
        "Completing the square rewrites x² + bx + c as (x + p)² + q. Take half the coefficient of x, square it, then balance the constant.",
      keyPoints: [
        "x² + bx + c = (x + b/2)² − (b/2)² + c.",
        "p = b/2.",
        "q = c − (b/2)².",
        "The turning point of y = (x + p)² + q is (−p, q).",
        "The minimum value of (x + p)² + q is q.",
        "If the coefficient of x² is not 1, factor it out first.",
      ],
      workedExamples: [
        {
          problem: "Express x² + 6x + 5 in completed-square form.",
          steps: [
            { step: 1, instruction: "Half the coefficient of x", working: "Half of 6 is 3." },
            { step: 2, instruction: "Square it", working: "3² = 9." },
            { step: 3, instruction: "Balance the expression", working: "(x + 3)² − 9 + 5 = (x + 3)² − 4." },
          ],
          answer: "(x + 3)² − 4",
        },
        {
          problem: "Solve x² + 6x + 5 = 0 by completing the square.",
          steps: [
            { step: 1, instruction: "Use the completed-square form", working: "(x + 3)² − 4 = 0." },
            { step: 2, instruction: "Rearrange", working: "(x + 3)² = 4." },
            { step: 3, instruction: "Square root both sides", working: "x + 3 = ±2." },
            { step: 4, instruction: "Solve", working: "x = −1 or x = −5." },
          ],
          answer: "x = −1 or x = −5",
        },
        {
          problem: "Find the turning point of y = x² − 8x + 13.",
          steps: [
            { step: 1, instruction: "Complete the square", working: "x² − 8x + 13 = (x − 4)² − 16 + 13 = (x − 4)² − 3." },
            { step: 2, instruction: "Read the turning point", working: "The turning point is (4, −3)." },
          ],
          answer: "(4, −3)",
        },
      ],
      whyItWorks:
        "A square such as (x + p)² is always at least 0. This makes completed-square form useful for solving, graphing and proving inequalities.",
    },
    practice: {
      warmUp: [
        {
          id: "2.14.w1",
          type: "text",
          question: "Express x² + 4x in completed-square form.",
          answer: "(x+2)^2 - 4",
          acceptedAnswers: ["(x+2)^2 - 4", "(x + 2)^2 - 4", "(x+2)² - 4", "(x + 2)² - 4"],
          hint1: "Half of 4 is 2.",
          hint2: "2² = 4.",
          hint3: "(x + 2)² − 4",
        },
        {
          id: "2.14.w2",
          type: "text",
          question: "What is the minimum value of (x − 3)² + 7?",
          answer: "7",
          acceptedAnswers: ["7"],
          hint1: "(x − 3)² is always at least 0.",
          hint2: "The minimum happens when x = 3.",
          hint3: "The minimum value is 7.",
        },
      ],
      level1: [
        {
          id: "2.14.1",
          type: "text",
          question: "Express x² + 10x + 21 in completed-square form.",
          answer: "(x+5)^2 - 4",
          acceptedAnswers: ["(x+5)^2 - 4", "(x + 5)^2 - 4", "(x+5)² - 4", "(x + 5)² - 4"],
          hint1: "Half of 10 is 5.",
          hint2: "(x + 5)² = x² + 10x + 25.",
          hint3: "x² + 10x + 21 = (x + 5)² − 4.",
        },
      ],
      level2: [
        {
          id: "2.14.2",
          type: "text",
          question: "Express x² − 6x + 11 in completed-square form.",
          answer: "(x-3)^2 + 2",
          acceptedAnswers: ["(x-3)^2 + 2", "(x - 3)^2 + 2", "(x-3)² + 2", "(x - 3)² + 2"],
          hint1: "Half of −6 is −3.",
          hint2: "(x − 3)² = x² − 6x + 9.",
          hint3: "x² − 6x + 11 = (x − 3)² + 2.",
        },
        {
          id: "2.14.3",
          type: "text",
          question: "Solve x² + 4x − 5 = 0 by completing the square. Give answers smallest first.",
          answer: "-5, 1",
          acceptedAnswers: ["-5, 1", "-5,1", "x=-5, x=1", "x = -5 or x = 1"],
          hint1: "x² + 4x − 5 = (x + 2)² − 9.",
          hint2: "(x + 2)² = 9.",
          hint3: "x + 2 = ±3, so x = −5 or x = 1.",
        },
      ],
      level3: [
        {
          id: "2.14.4",
          type: "text",
          question: "Find the coordinates of the turning point of y = x² − 4x + 7.",
          answer: "(2,3)",
          acceptedAnswers: ["(2,3)", "(2, 3)"],
          hint1: "Complete the square.",
          hint2: "x² − 4x + 7 = (x − 2)² + 3.",
          hint3: "The turning point is (2, 3).",
        },
        {
          id: "2.14.5",
          type: "text",
          question: "Find the minimum value of x² + 2x + 5.",
          answer: "4",
          acceptedAnswers: ["4"],
          hint1: "Complete the square.",
          hint2: "x² + 2x + 5 = (x + 1)² + 4.",
          hint3: "The minimum value is 4.",
        },
      ],
    },
    aiChallenge: {
      prompt:
        "Show that x² − 6x + 13 > 0 for all real values of x. Use completing the square in your proof.",
      successCriteria: [
        "Completes the square to get (x − 3)² + 4.",
        "States that (x − 3)² ≥ 0 for all real x.",
        "Adds 4 to conclude the expression is at least 4.",
        "States clearly that the expression is greater than 0 for all real x.",
      ],
    },
  },
];

export default algebraTopics;